import React from 'react';
import { styled, Typography } from '@mui/material';
import { format, isValid } from 'date-fns';
import { moneyFormat } from 'helpers/moneyFormat';
import { BudgetLog } from 'types/budgetPayment';

const Item = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '120px minmax(0, 1fr)',
  columnGap: theme.spacing(1.5),
  padding: theme.spacing(0.75, 0),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
  },
}));

const VALUE_LABELS: Record<string, string> = {
  value_doctor: 'Médico',
  value_hospital: 'Hospital',
  value_anesthetist: 'Anestesista',
  value_packages: 'Pacotes',
  value_total: 'Venda',
};

type Change = { from: number; to: number };

function formatDateTime(value: string) {
  const date = new Date(value.replace(' ', 'T'));
  return isValid(date) ? format(date, 'dd/MM/yyyy HH:mm') : '-';
}

function formatDay(value?: string) {
  if (!value) return '-';

  const [year, month, day] = value.split('-');
  return `${day}/${month}/${year}`;
}

function describeChanges(changes: Record<string, Change | Record<string, Change>>) {
  const range = ({ from, to }: Change) => `de ${moneyFormat(from)} para ${moneyFormat(to)}`;

  return Object.entries(changes)
    .flatMap(([field, change]) =>
      field === 'packages'
        ? Object.entries(change as Record<string, Change>).map(([name, item]) => `pacote ${name} ${range(item)}`)
        : [`${VALUE_LABELS[field] || field} ${range(change as Change)}`],
    )
    .join('; ');
}

function describe(log: BudgetLog): string {
  const data = log.data || {};

  switch (log.action) {
    case 'budget_created':
      return `Criou o orçamento de ${moneyFormat(data.value_total || 0)}`;
    case 'budget_updated':
      return `Alterou ${describeChanges(data.changes || {})}`;
    case 'payment_created':
      return `Informou pagamento de ${moneyFormat(data.value)} (${data.payment_method || 'forma não informada'}), recebido em ${formatDay(data.paid_at)}`;
    case 'payment_reversed':
      return `Estornou pagamento de ${moneyFormat(data.value)}. Motivo: ${data.reason}`;
    case 'budget_closed':
      return 'Fechou o orçamento';
    case 'budget_deleted':
      return 'Cancelou o orçamento';
    default:
      return log.action;
  }
}

const BudgetHistory: React.FC<{ logs: BudgetLog[] }> = ({ logs }) => {
  if (logs.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        Nenhum registro ainda.
      </Typography>
    );
  }

  return (
    <div>
      {logs.map(log => (
        <Item key={log.id}>
          <Typography variant="caption" color="textSecondary">
            {formatDateTime(log.created_at)}
          </Typography>
          <Typography variant="body2">
            <strong>{log.user}</strong> · {describe(log)}
          </Typography>
        </Item>
      ))}
    </div>
  );
};

export default BudgetHistory;
